import { BentoGrid, BentoCell, GlassCard, KYCBadge } from "@kj/design-system";
import { Heart, Languages, ShieldCheck, Sparkles, Video } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function HomePage() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-8">
      {/* Hero */}
      <section className="relative">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <GlassCard radius="2xl" padding="lg" className="relative">
            <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary-500/30 blur-3xl" />
            <div className="pointer-events-none absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-trust-500/20 blur-3xl" />
            <p className="text-xs uppercase tracking-widest text-fg-muted">
              {t("home.hero.eyebrow")}
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
              {t("home.hero.title")}
            </h1>
            <p className="mt-3 max-w-2xl text-fg-muted">
              {t("home.hero.sub")}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-white shadow-glass-md transition-transform hover:-translate-y-0.5"
              >
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                {t("cta.start")}
              </button>
              <KYCBadge label="Enhanced KYC" />
            </div>
          </GlassCard>
        </motion.div>
      </section>

      {/* Bento features */}
      <section>
        <BentoGrid>
          <BentoCell
            tone="primary"
            span="md"
            height="md"
            eyebrow="Realtime AI"
            title={t("home.feature.translate.title")}
            icon={<Languages className="h-4 w-4" />}
          >
            <p className="text-sm text-fg-muted">
              {t("home.feature.translate.sub")}
            </p>
            <div className="mt-4 flex items-center gap-2 text-xs text-fg-muted">
              <span className="rounded-full bg-white/5 px-2 py-0.5">
                BLEU ≥ 48
              </span>
              <span className="rounded-full bg-white/5 px-2 py-0.5">
                p95 ≤ 400ms
              </span>
            </div>
          </BentoCell>

          <BentoCell
            tone="trust"
            span="sm"
            height="md"
            eyebrow="Video"
            title={t("home.feature.video.title")}
            icon={<Video className="h-4 w-4" />}
          >
            <p className="text-sm text-fg-muted">
              {t("home.feature.video.sub")}
            </p>
          </BentoCell>

          <BentoCell
            span="sm"
            height="sm"
            eyebrow="Trust"
            title={t("home.feature.trust.title")}
            icon={<ShieldCheck className="h-4 w-4" />}
          >
            <p className="text-sm text-fg-muted">
              {t("home.feature.trust.sub")}
            </p>
          </BentoCell>

          <BentoCell
            tone="sakura"
            span="md"
            height="md"
            eyebrow="Exclusive"
            title={t("home.feature.niche.title")}
            icon={<Heart className="h-4 w-4" />}
          >
            <p className="text-sm text-fg-muted">
              {t("home.feature.niche.sub")}
            </p>
          </BentoCell>

          <BentoCell span="lg" height="sm" eyebrow="KPI" title="SLO Dashboard (preview)">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Stat label="메시지 p95" value="~ 380ms" />
              <Stat label="음성 p50" value="~ 820ms" />
              <Stat label="자막 p95" value="~ 1.1s" />
              <Stat label="BLEU ko↔ja" value="48.6" />
            </div>
          </BentoCell>
        </BentoGrid>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-white/5 px-3 py-2">
      <div className="text-[0.7rem] uppercase tracking-wider text-fg-muted">
        {label}
      </div>
      <div className="mt-0.5 font-semibold">{value}</div>
    </div>
  );
}
